// ============================================================================
// ConfirmContext — the app's two confirmation overlays and their openers.
// ============================================================================
// Extracted from App() in B2.2c. App owned `deleteConfirm` / `confirmAction`
// state, built the two modal ELEMENTS from it, and then handed those elements
// to Header as props so Header could render them. That is the last thing that
// forced Header to be an App-bound wrapper rather than a plain import, and it
// is why 49 view mounts had to be handed a `Header` component prop.
//
// The provider now owns the state, exposes the two openers, and renders both
// overlays itself — after `children`, so they sit above the app content in the
// top destructive overlay tier the design system requires.
//
//   confirmDelete(message, onConfirm)                        — destructive
//   confirmActionPrompt(message, onConfirm, confirmLabel)    — non-destructive
//
// The two `window._wcfConfirmDelete` / `window._wcfConfirm` globals are kept
// verbatim: several extracted views still reach for them instead of taking a
// prop, and removing them is a separate change with its own guards.
//
// THE SIBLING RENDER IS LOAD-BEARING. Both overlays are siblings of `children`
// inside the same Provider, which is what the eviction-safety invariant relies
// on; the TypeScript conversion changed no part of that JSX.
// ============================================================================
import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import DeleteModal from '../shared/DeleteModal.jsx';
import ConfirmModal from '../shared/ConfirmModal.jsx';

// The two request objects, and they are genuinely KNOWN rather than guessed:
// this module is the only thing that ever builds them (in the two openers
// below), and each field is handed straight to a prop that the already-
// converted DeleteModal.tsx / ConfirmModal.tsx declare — `msg?: ReactNode`,
// `onConfirm: () => void`, `confirmLabel?: string`. The compiler checks that
// agreement at the two React.createElement calls.
type DeleteRequest = {message: React.ReactNode; onConfirm: () => void};
type ConfirmActionRequest = {message: React.ReactNode; onConfirm: () => void; confirmLabel?: string};

// The two globals this provider publishes. Declared rather than reached for
// through a cast, so the assignments below are ordinary checked property
// writes and any TypeScript reader of `window._wcfConfirm` gets the real
// signature. Optional because they exist only while a ConfirmProvider is
// mounted — the public webform entries never mount one.
declare global {
  interface Window {
    _wcfConfirmDelete?: (message: React.ReactNode, onConfirm: () => void) => void;
    _wcfConfirm?: (message: React.ReactNode, onConfirm: () => void, confirmLabel?: string) => void;
  }
}

// DECLARED, not inferred — the one context in this wave where that is the
// better answer. Inferring would mean hoisting the state into a value-building
// hook, but this provider's JSX reads `deleteConfirm`/`setDeleteConfirm`
// directly to render the two overlays as siblings of `children`, so the hook
// would have to hand back both the value and the setters and the JSX would have
// to be rewritten around it. That JSX is exactly what must not move. The type
// costs nothing in honesty here: all four members are built in this file, and
// `useMemo<ConfirmContextValue>` below contextually types the literal, so a
// missing or extra member is a compile error rather than silent drift.
type ConfirmContextValue = {
  confirmDelete: (message: React.ReactNode, onConfirm: () => void) => void;
  confirmActionPrompt: (message: React.ReactNode, onConfirm: () => void, confirmLabel?: string) => void;
  deleteConfirm: DeleteRequest | null;
  confirmAction: ConfirmActionRequest | null;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({children}: {children?: React.ReactNode}) {
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteRequest | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmActionRequest | null>(null);

  const confirmDelete = useCallback((message: React.ReactNode, onConfirm: () => void) => {
    setDeleteConfirm({message, onConfirm});
  }, []);

  // Non-destructive confirm (no typed gate) for side-effect actions that
  // aren't deletes. Mirrors confirmDelete shape; an optional confirmLabel
  // sets the action-button text.
  const confirmActionPrompt = useCallback((message: React.ReactNode, onConfirm: () => void, confirmLabel?: string) => {
    setConfirmAction({message, onConfirm, confirmLabel});
  }, []);

  // Expose globally so child components can use without prop drilling
  useEffect(() => {
    window._wcfConfirmDelete = confirmDelete;
  }, [confirmDelete]);
  useEffect(() => {
    window._wcfConfirm = confirmActionPrompt;
  }, [confirmActionPrompt]);

  const value = useMemo<ConfirmContextValue>(
    () => ({confirmDelete, confirmActionPrompt, deleteConfirm, confirmAction}),
    [confirmDelete, confirmActionPrompt, deleteConfirm, confirmAction],
  );

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {deleteConfirm
        ? React.createElement(DeleteModal, {
            msg: deleteConfirm.message,
            onConfirm: deleteConfirm.onConfirm,
            onCancel: () => setDeleteConfirm(null),
          })
        : null}
      {confirmAction
        ? React.createElement(ConfirmModal, {
            msg: confirmAction.message,
            confirmLabel: confirmAction.confirmLabel,
            onConfirm: confirmAction.onConfirm,
            onCancel: () => setConfirmAction(null),
          })
        : null}
    </ConfirmContext.Provider>
  );
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useConfirm(): ConfirmContextValue {
  const value = useContext(ConfirmContext);
  if (!value) throw new Error('useConfirm must be used within ConfirmProvider');
  return value;
}
